/**
 * Per-route document head: title, description, canonical, Open Graph, Twitter
 * and JSON-LD. Implemented with direct DOM writes so the app carries no head
 * management dependency.
 *
 * Every tag this module owns is marked `data-hp-head`, so a navigation can
 * remove what the previous route added without disturbing the tags that ship
 * in index.html (those are upserted in place instead).
 */
import {
  SITE_ORIGIN,
  chrome,
  firstImageOf,
  getImage,
  largestRendition,
  leadTextOf,
  isoDate,
  toMediaPath,
  type PageMeta,
} from './content';

export const SITE_NAME = 'HEALTH PRIME®';
export const DEFAULT_OG_IMAGE = '/brand/og-health-prime-v2.jpg';
export const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large';

export type Crumb = { name: string; url: string };

export type HeadMeta = {
  title: string;
  description: string;
  canonical: string;
  robots: string;
  ogType: 'website' | 'article';
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  publishedTime?: string;
  modifiedTime?: string;
  jsonLd?: Record<string, unknown>[];
  language?: string;
  alternates?: { language: string; href: string }[];
};

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE_ORIGIN + (pathOrUrl.startsWith('/') ? pathOrUrl : '/' + pathOrUrl);
}

/** Resolve an Open Graph image to a file that actually exists in this build. */
export function resolveOgImage(page?: PageMeta): string {
  if (page?.route === '/') return absoluteUrl(DEFAULT_OG_IMAGE);
  const candidates: string[] = [];
  if (page?.seo?.ogImage) candidates.push(page.seo.ogImage);
  if (page) {
    const first = firstImageOf(page);
    if (first) candidates.push(first);
  }
  for (const candidate of candidates) {
    const asset = getImage(toMediaPath(candidate));
    if (asset) return absoluteUrl(largestRendition(asset));
  }
  return absoluteUrl(DEFAULT_OG_IMAGE);
}

/* -------------------------------------------------------------------------- */
/* JSON-LD                                                                    */
/* -------------------------------------------------------------------------- */

const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

export function organizationNode(): Record<string, unknown> {
  const { address } = chrome.footer;
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: address.company,
    taxID: address.uid,
    alternateName: SITE_NAME,
    url: SITE_ORIGIN + '/',
    logo: {
      '@type': 'ImageObject',
      url: absoluteUrl(chrome.brand.logo),
    },
    email: address.email,
    telephone: address.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address.street,
      postalCode: address.city.replace(/^CH-/, '').split(/\s+/)[0],
      addressLocality: address.city.replace(/^(?:CH-)?\d+\s*/, ''),
      addressCountry: 'CH',
    },
  };
}

export function websiteNode(): Record<string, unknown> {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE_ORIGIN + '/',
    name: SITE_NAME,
    inLanguage: 'de-CH',
    publisher: { '@id': ORGANIZATION_ID },
  };
}

export function breadcrumbNode(crumbs: Crumb[]): Record<string, unknown> {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.url),
    })),
  };
}

export function articleNode(page: PageMeta, ogImage: string): Record<string, unknown> {
  return {
    '@type': 'Article',
    '@id': `${absoluteUrl(page.route)}#article`,
    headline: page.title,
    description: page.seo?.description || leadTextOf(page, 200),
    datePublished: isoDate(page.date),
    dateModified: isoDate(page.modified || page.date),
    inLanguage: 'de-CH',
    mainEntityOfPage: absoluteUrl(page.route),
    image: ogImage,
    author: { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
    keywords: page.terms
      .filter((t) => t.taxonomy === 'post_tag')
      .map((t) => t.name)
      .join(', '),
    articleSection: page.terms
      .filter((t) => t.taxonomy === 'category')
      .map((t) => t.name),
  };
}

/* -------------------------------------------------------------------------- */
/* Meta derivation                                                            */
/* -------------------------------------------------------------------------- */

function titleFor(page: PageMeta): string {
  if (page.seo?.title) return page.seo.title;
  if (page.route === '/') return `${SITE_NAME} — ${page.title}`;
  return `${page.title} – Health Prime`;
}

/** Routes that must never be indexed, regardless of what the source said. */
const NOINDEX_ROUTES = new Set(['/my-instagram-feed', '/sales-dashboard']);

export function metaForPage(page: PageMeta, crumbs: Crumb[] = []): HeadMeta {
  const canonical = absoluteUrl(page.route);
  const description = page.seo?.description || leadTextOf(page, 200);
  const ogImage = resolveOgImage(page);
  const isArticle = page.type === 'post';
  const robots =
    NOINDEX_ROUTES.has(page.route) || page.status !== 'publish'
      ? 'noindex, nofollow'
      : page.seo?.robots || DEFAULT_ROBOTS;

  const jsonLd: Record<string, unknown>[] = [];
  if (isArticle) jsonLd.push(articleNode(page, ogImage));
  if (crumbs.length > 1) jsonLd.push(breadcrumbNode(crumbs));

  return {
    title: titleFor(page),
    description,
    canonical,
    robots,
    ogType: isArticle ? 'article' : 'website',
    ogTitle: page.seo?.ogTitle || titleFor(page),
    ogDescription: page.seo?.ogDescription || description,
    ogImage,
    publishedTime: isArticle ? isoDate(page.date) : undefined,
    modifiedTime: isArticle ? isoDate(page.modified || page.date) : undefined,
    jsonLd,
  };
}

export function metaForListing(options: {
  title: string;
  description: string;
  route: string;
  robots?: string;
  crumbs?: Crumb[];
}): HeadMeta {
  const jsonLd: Record<string, unknown>[] = [];
  if (options.crumbs && options.crumbs.length > 1) {
    jsonLd.push(breadcrumbNode(options.crumbs));
  }
  return {
    title: options.title,
    description: options.description,
    canonical: absoluteUrl(options.route),
    robots: options.robots ?? DEFAULT_ROBOTS,
    ogType: 'website',
    ogTitle: options.title,
    ogDescription: options.description,
    ogImage: absoluteUrl(DEFAULT_OG_IMAGE),
    jsonLd,
  };
}

/* -------------------------------------------------------------------------- */
/* The tag set                                                                */
/* -------------------------------------------------------------------------- */

/**
 * One tag this module owns. `applyHead` writes these into `document.head` at
 * runtime; `src/entry-server.tsx` serialises the same list into the static
 * HTML at build time. Both read this one function, so a prerendered head and a
 * client-navigated head can never drift apart.
 */
export type HeadTag =
  | { el: 'meta'; key: 'name' | 'property'; id: string; content: string }
  | { el: 'link'; rel: string; href: string; hreflang?: string };

/**
 * Every `<meta>` this module owns, whether or not the current route has one.
 * `applyHead` clears the ones a route leaves out, and the prerender strips the
 * shell's copies before writing the route's own. One list, three readers.
 */
export const MANAGED_META_IDS: readonly string[] = [
  'description',
  'robots',
  'og:type',
  'og:site_name',
  'og:locale',
  'og:url',
  'og:title',
  'og:description',
  'og:image',
  'twitter:card',
  'twitter:title',
  'twitter:description',
  'twitter:image',
  'article:published_time',
  'article:modified_time',
];

/**
 * Every tag `applyHead` manages, in document order. Pure: no DOM, no globals.
 *
 * A tag with nothing to say is left out rather than written empty: /news is a
 * bare listing with no prose anywhere in its source, and an empty description
 * is a worse signal than no description at all.
 */
export function headTagsFor(meta: HeadMeta): HeadTag[] {
  const all: HeadTag[] = [
    { el: 'meta', key: 'name', id: 'description', content: meta.description },
    { el: 'meta', key: 'name', id: 'robots', content: meta.robots },
    { el: 'link', rel: 'canonical', href: meta.canonical },

    { el: 'meta', key: 'property', id: 'og:type', content: meta.ogType },
    { el: 'meta', key: 'property', id: 'og:site_name', content: SITE_NAME },
    { el: 'meta', key: 'property', id: 'og:locale', content: meta.language?.replace('-', '_') || 'de_CH' },
    { el: 'meta', key: 'property', id: 'og:url', content: meta.canonical },
    { el: 'meta', key: 'property', id: 'og:title', content: meta.ogTitle },
    { el: 'meta', key: 'property', id: 'og:description', content: meta.ogDescription },
    { el: 'meta', key: 'property', id: 'og:image', content: meta.ogImage },

    { el: 'meta', key: 'name', id: 'twitter:card', content: 'summary_large_image' },
    { el: 'meta', key: 'name', id: 'twitter:title', content: meta.ogTitle },
    { el: 'meta', key: 'name', id: 'twitter:description', content: meta.ogDescription },
    { el: 'meta', key: 'name', id: 'twitter:image', content: meta.ogImage },
  ];
  for (const alternate of meta.alternates ?? []) {
    all.push({ el: 'link', rel: 'alternate', href: alternate.href, hreflang: alternate.language });
  }
  if (meta.publishedTime) {
    all.push({
      el: 'meta',
      key: 'property',
      id: 'article:published_time',
      content: meta.publishedTime,
    });
  }
  if (meta.modifiedTime) {
    all.push({
      el: 'meta',
      key: 'property',
      id: 'article:modified_time',
      content: meta.modifiedTime,
    });
  }
  return all.filter((tag) => (tag.el === 'meta' ? tag.content !== '' : tag.href !== ''));
}

/** The single JSON-LD `@graph` for a route. Pure, like `headTagsFor`. */
export function jsonLdFor(
  meta: HeadMeta,
  extraJsonLd: Record<string, unknown>[] = [],
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [organizationNode(), websiteNode(), ...extraJsonLd, ...(meta.jsonLd ?? [])],
  };
}

/* -------------------------------------------------------------------------- */
/* Prerender capture                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The prerender needs the head of the route it is rendering, and only the
 * components know it. `src/entry-server.tsx` installs a sink here; `<Head>`
 * reports to it during render. In the browser the sink is always null, so this
 * costs one null check per render and changes nothing.
 */
type HeadSink = (meta: HeadMeta) => void;
let headSink: HeadSink | null = null;

export function setHeadSink(sink: HeadSink | null): void {
  headSink = sink;
}

export function captureHead(meta: HeadMeta): void {
  headSink?.(meta);
}

/* -------------------------------------------------------------------------- */
/* DOM application                                                            */
/* -------------------------------------------------------------------------- */

function upsert(spec: HeadTag): void {
  const head = document.head;
  if (spec.el === 'meta') {
    const selector = `meta[${spec.key}="${spec.id}"]`;
    let node = head.querySelector<HTMLMetaElement>(selector);
    if (!node) {
      node = document.createElement('meta');
      node.setAttribute(spec.key, spec.id);
      node.setAttribute('data-hp-head', '');
      head.appendChild(node);
    }
    node.setAttribute('content', spec.content);
    return;
  }
  const selector = `link[rel="${spec.rel}"]${spec.hreflang ? `[hreflang="${spec.hreflang}"]` : ''}`;
  let node = head.querySelector<HTMLLinkElement>(selector);
  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', spec.rel);
    node.setAttribute('data-hp-head', '');
    head.appendChild(node);
  }
  node.setAttribute('href', spec.href);
  if (spec.hreflang) node.setAttribute('hreflang', spec.hreflang);
}

function normalizedPath(routeOrUrl: string): string | null {
  if (!routeOrUrl.trim()) return null;
  try {
    const path = new URL(routeOrUrl, window.location.origin).pathname;
    return path === '/' ? path : path.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/**
 * Social-card documents inject an original-page link and ImageObject JSON-LD.
 * Keep those nodes when the client hydrates that same route, but discard them
 * as soon as SPA navigation applies the head of another canonical page.
 */
function removeStaleShareCardHead(canonical: string): void {
  const currentPath = normalizedPath(canonical);
  const injected = document.head.querySelectorAll<HTMLElement>(
    'link[data-share-card-route], script[data-share-card-route]',
  );
  for (const node of injected) {
    const nodePath = normalizedPath(node.getAttribute('data-share-card-route') ?? '');
    if (currentPath === null || nodePath !== currentPath) node.remove();
  }
}

export function applyHead(meta: HeadMeta, extraJsonLd: Record<string, unknown>[] = []): void {
  document.title = meta.title;
  document.documentElement.lang = meta.language ?? 'de-CH';
  document.head.querySelectorAll('link[rel="alternate"][hreflang][data-hp-head]').forEach((node) => node.remove());
  removeStaleShareCardHead(meta.canonical);

  const tags = headTagsFor(meta);
  // Anything this route leaves out has to go, or a navigation from a post to a
  // page would keep the post's article times and a page without a description
  // would keep the previous one.
  const written = new Set(tags.map((t) => (t.el === 'meta' ? t.id : null)));
  for (const id of MANAGED_META_IDS) {
    if (written.has(id)) continue;
    const node = document.head.querySelector(`meta[name="${id}"], meta[property="${id}"]`);
    if (node) node.remove();
  }
  for (const tag of tags) upsert(tag);

  let script = document.head.querySelector<HTMLScriptElement>(
    'script[data-hp-jsonld]',
  );
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-hp-jsonld', '');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(jsonLdFor(meta, extraJsonLd));
}
